import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { GAME_DEFAULTS } from '../lib/constants.js'
import HostHeader from '../components/ui/HostHeader.jsx'
import HostSideNav from '../components/ui/HostSideNav.jsx'
import SafeHtml from '../components/ui/SafeHtml.jsx'
import ArcGauge from '../components/ui/ArcGauge.jsx'
import ParticipantStatsGrid from '../components/host/ParticipantStatsGrid.jsx'
import ParticipantDetail from '../components/host/ParticipantDetail.jsx'

const POLL_MS = 2000

function formatDateTime(isoStr) {
  if (!isoStr) return '—'
  const d = new Date(isoStr)
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) + ' ' +
         d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}

function formatDuration(start, end) {
  if (!start) return '—'
  const ms = Math.max(0, (end ? new Date(end) : new Date()) - new Date(start))
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`
  if (m > 0) return `${m}min ${String(s).padStart(2, '0')}s`
  return `${s}s`
}

// ── Carte Global (même format que StatCard, non interactive) ────────────────
function GlobalCard({ correctes, timeouts, incorrectes, total }) {
  return (
    <div className="bg-gray-800 border border-white/10 rounded-2xl p-3 flex flex-col items-center gap-1 w-full">
      <p className="text-white text-xs text-center w-full leading-tight font-semibold">Global</p>
      <ArcGauge correctes={correctes} timeouts={timeouts} incorrectes={incorrectes} total={total} />
      <p className="text-white text-xs">{correctes} ✅&nbsp; {timeouts} ⌛&nbsp; {incorrectes} ❌</p>
    </div>
  )
}

// ── Carte Légende ─────────────────────────────────────────────────────────────
function LegendCard() {
  return (
    <div className="bg-gray-800 border border-white/10 rounded-2xl p-4 flex flex-col justify-center gap-2">
      <div className="text-white/70 text-xs font-medium">✅ Bonnes réponses</div>
      <div className="flex flex-col gap-1.5 pl-3 border-l-2 border-white/10">
        <div className="flex items-center gap-2">
          <span className="shrink-0 inline-block w-2.5 h-2.5 rounded-full bg-green-500" />
          <span className="text-white/55 text-[11px]">≥ 70% — Maîtrisé</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="shrink-0 inline-block w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="text-white/55 text-[11px]">50–69% — Acquis</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="shrink-0 inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
          <span className="text-white/55 text-[11px]">&lt; 50% — À réviser</span>
        </div>
      </div>
      <div className="border-t border-white/10 my-0.5" />
      <div className="flex items-center gap-2.5">
        <span className="shrink-0 inline-block w-3 h-3 rounded-full bg-slate-200" />
        <span className="text-white/70 text-xs">⌛ Délai dépassé</span>
      </div>
      <div className="flex items-center gap-2.5">
        <span className="shrink-0 inline-block w-3 h-3 rounded-full bg-gray-600 border border-white/20" />
        <span className="text-white/70 text-xs">❌ Mauvaises réponses</span>
      </div>
    </div>
  )
}

function formatMs(ms) {
  if (!ms && ms !== 0) return '—'
  const totalSec = Math.floor(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`
  return `${s}s`
}

// ── Carte Indicateurs secondaires ─────────────────────────────────────────────
function MetaCard({ launchAt, endAt, nbJoueurs }) {
  const items = [
    { emoji: '📅', label: 'Lancement', value: formatDateTime(launchAt) },
    { emoji: '⏱️', label: 'Durée',     value: formatDuration(launchAt, endAt) },
    { emoji: '👥', label: 'Joueurs',  value: nbJoueurs != null ? String(nbJoueurs) : '—' },
  ]
  return (
    <div className="bg-gray-800 border border-white/10 rounded-2xl p-4 flex flex-col justify-center">
      {items.map((item, i) => (
        <div key={item.label}>
          {i > 0 && <div className="border-t border-white/10 my-2.5" />}
          <div className="flex items-center gap-3">
            <span className="text-3xl leading-none shrink-0 w-10 text-center">{item.emoji}</span>
            <div>
              <div className="text-white/40 text-xs">{item.label}</div>
              <div className="text-white font-semibold text-base">{item.value}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Carte Indicateurs solo (remplace Joueurs par Meilleur temps) ──────────────
function SoloMetaCard({ launchAt, endAt, soloResult }) {
  const isRecord   = soloResult?.is_record   ?? false
  const previousMs = soloResult?.previous_ms ?? null
  const totalMs    = soloResult?.total_ms    ?? null
  const bestMs     = isRecord ? totalMs : previousMs

  let recordValue
  if (totalMs === null) {
    recordValue = <span className="text-white font-semibold">—</span>
  } else if (previousMs === null) {
    // Première course : juste la durée
    recordValue = <span className="text-white font-semibold">{formatMs(totalMs)}</span>
  } else if (isRecord) {
    // Nouveau record !
    recordValue = (
      <div>
        <span className="text-green-400 font-semibold">{formatMs(totalMs)}</span>
        <span className="text-green-300 text-xs block">🎉 Record battu !</span>
      </div>
    )
  } else {
    // Pas un record — afficher le meilleur + l'écart
    const diff = totalMs - previousMs
    recordValue = (
      <div>
        <span className="text-white font-semibold">{formatMs(bestMs)}</span>
        <span className="text-orange-300 text-xs block">+{formatMs(diff)} vs record</span>
      </div>
    )
  }

  const items = [
    { emoji: '📅', label: 'Lancement',     value: formatDateTime(launchAt) },
    { emoji: '⏱️', label: 'Durée',          value: formatDuration(launchAt, endAt) },
    { emoji: '🏆', label: 'Meilleur temps', value: recordValue },
  ]
  return (
    <div className="bg-gray-800 border border-white/10 rounded-2xl p-4 flex flex-col justify-center">
      {items.map((item, i) => (
        <div key={item.label}>
          {i > 0 && <div className="border-t border-white/10 my-2.5" />}
          <div className="flex items-center gap-3">
            <span className="text-3xl leading-none shrink-0 w-10 text-center">{item.emoji}</span>
            <div>
              <div className="text-white/40 text-xs">{item.label}</div>
              <div className="text-white font-semibold text-base">{item.value}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Carte sous-thème ou question ─────────────────────────────────────────────
// Titre au-dessus de la jauge. isCategory=true → titre en gras.
function StatCard({ title, correctes, timeouts, incorrectes, total, isCategory = false, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-gray-800 border border-white/10 rounded-2xl p-3 flex flex-col items-center gap-1 hover:border-white/30 transition-colors w-full"
    >
      <p className={`text-white text-xs text-center line-clamp-2 w-full leading-tight ${isCategory ? 'font-semibold' : 'font-medium'}`}>
        <SafeHtml html={title} />
      </p>
      <ArcGauge correctes={correctes} timeouts={timeouts} incorrectes={incorrectes} total={total} />
      <p className="text-white text-xs">{correctes} ✅&nbsp; {timeouts} ⌛&nbsp; {incorrectes} ❌</p>
    </button>
  )
}

// ── Bandeau de navigation (Global / Catégorie / Question) ────────────────────
// onBack=null → pas de chevron. breadcrumb = tableau de strings pour afficher le fil.
function NavBanner({ label, correctes, timeouts, incorrectes, total, breadcrumb, onBack, legend = false, meta = null }) {
  return (
    <div className="bg-gray-800 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
      {onBack && (
        <button
          onClick={onBack}
          className="text-white/50 hover:text-white transition-colors text-4xl font-thin leading-none shrink-0 pb-1"
          aria-label="Retour"
        >
          ‹
        </button>
      )}
      <ArcGauge correctes={correctes} timeouts={timeouts} incorrectes={incorrectes} total={total} size={90} />
      <div className="flex-1 min-w-0">
        {breadcrumb && (
          <div className="text-white/35 text-xs mb-0.5">{breadcrumb}</div>
        )}
        <p className="text-white font-semibold text-sm line-clamp-3"><SafeHtml html={label} /></p>
        <p className="text-white text-xs mt-1">
          {correctes} ✅&nbsp; {timeouts} ⌛&nbsp; {incorrectes} ❌
        </p>
        {legend && (
          <div className="flex gap-3 mt-1 text-xs text-white/35">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
              <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
              Bonnes réponses
            </span>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-slate-200" />Délai dépassé</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-gray-700 border border-white/20" />Mauvaises réponses</span>
          </div>
        )}
      </div>
      {meta && (
        <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0 text-right border-l border-white/10 pl-3">
          {meta.map(item => (
            <div key={item.label} className="text-xs leading-tight">
              <div className="text-white/40">{item.label}</div>
              <div className="text-white font-medium">{item.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Carte réponse ────────────────────────────────────────────────────────────
function AnswerCard({ texte, count, total, isCorrect }) {
  const pct    = total > 0 ? Math.round(count / total * 100) : 0
  const border = isCorrect ? 'border-[3px] border-green-500' : 'border border-white/10'
  return (
    <div className={`bg-gray-800 ${border} rounded-2xl p-4 flex flex-col gap-2`}>
      <div className="flex items-start justify-between gap-2">
        <SafeHtml html={texte || '—'} className="text-white text-base leading-snug flex-1" />
        {isCorrect && <span className="text-xl leading-none shrink-0">✅</span>}
      </div>
      <div className="mt-1">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-white/40">{count} joueur{count !== 1 ? 's' : ''}</span>
          <span className={`font-semibold ${isCorrect ? 'text-green-400' : 'text-white/60'}`}>{pct}%</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${isCorrect ? 'bg-green-500' : 'bg-white/30'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  )
}

// ── Page principale ──────────────────────────────────────────────────────────
export default function StatsPage() {
  const { code }   = useParams()
  const navigate   = useNavigate()

  const [game, setGame]   = useState(null)
  const [stats, setStats] = useState(null)
  const [drill, setDrill] = useState(null) // null | { level:'st', ...st } | { level:'q', stId, stNom, ...q }
  const [mode, setMode]   = useState('global') // 'global' | 'par-joueur'
  const [participants, setParticipants]           = useState(null)
  const [selectedParticipant, setSelectedParticipant] = useState(null)
  const pollingRef             = useRef(null)
  const participantsPollingRef = useRef(null)

  useEffect(() => {
    apiFetch(`/api/games/${code}`)
      .then(setGame)
      .catch(() => navigate('/', { replace: true }))
  }, [code])

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/games/${code}/stats`)
      setStats(data)
      setDrill(prev => {
        if (!prev) return prev
        if (prev.level === 'st') {
          const st = data.sous_themes.find(s => s.id === prev.id)
          return st ? { ...prev, ...st } : prev
        }
        if (prev.level === 'q') {
          const st = data.sous_themes.find(s => s.id === prev.stId)
          const q  = st?.questions.find(q => q.id === prev.id)
          return q ? { ...prev, ...q } : prev
        }
        return prev
      })
    } catch (_) {}
  }, [code])

  useEffect(() => {
    fetchStats()
    pollingRef.current = setInterval(fetchStats, POLL_MS)
    return () => clearInterval(pollingRef.current)
  }, [fetchStats])

  const fetchParticipants = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/games/${code}/participants-stats`)
      setParticipants(data.participants)
    } catch (_) {}
  }, [code])

  function handleModeChange(newMode) {
    setMode(newMode)
    setSelectedParticipant(null)
    clearInterval(participantsPollingRef.current)
    if (newMode === 'par-joueur') {
      fetchParticipants()
      participantsPollingRef.current = setInterval(fetchParticipants, POLL_MS)
    }
  }

  useEffect(() => () => clearInterval(participantsPollingRef.current), [])

  const isSolo = game?.config?.solo === true

  function handleGameUpdate(type, data) {
    if (type === 'end') { navigate(isSolo ? '/home' : `/podium/${code}`, { replace: true }); return }
    if (data?.statut) setGame(g => ({ ...g, statut: data.statut }))
  }

  const statut   = game?.statut ?? null

  // ── Rendu de la vue "par joueur" ────────────────────────────────────────────
  function renderParJoueur() {
    if (!participants) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-3 bg-gray-800 rounded-2xl p-8">
            <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-white/70 text-sm">Chargement…</p>
          </div>
        </div>
      )
    }
    if (selectedParticipant) {
      const idx   = participants.findIndex(p => p.kart.id === selectedParticipant.kart.id)
      const onPrev = idx > 0 ? () => setSelectedParticipant(participants[idx - 1]) : null
      const onNext = idx < participants.length - 1 ? () => setSelectedParticipant(participants[idx + 1]) : null
      return (
        <ParticipantDetail
          participant={selectedParticipant}
          onBack={() => setSelectedParticipant(null)}
          onPrev={onPrev}
          onNext={onNext}
        />
      )
    }
    return (
      <ParticipantStatsGrid
        participants={participants}
        onSelect={setSelectedParticipant}
      />
    )
  }

  // ── Rendu du contenu principal ─────────────────────────────────────────────
  function renderContent() {
    if (!stats) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-3 bg-gray-800 rounded-2xl p-8">
            <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-white/70 text-sm">Chargement…</p>
          </div>
        </div>
      )
    }
    if (stats.global.total === 0) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-3 bg-gray-800 rounded-2xl p-8 text-white/70">
            <span className="text-4xl">📊</span>
            <p className="text-sm">Aucune réponse enregistrée pour l'instant.</p>
          </div>
        </div>
      )
    }

    const g = stats.global

    // Niveau 0 — Grille sous-thèmes
    if (!drill) {
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <GlobalCard
              correctes={g.correctes}
              timeouts={g.timeouts ?? 0}
              incorrectes={g.incorrectes ?? 0}
              total={g.total}
            />
            <LegendCard />
            {isSolo ? (
              <SoloMetaCard
                launchAt={game?.cree_le}
                endAt={game?.termine_le}
                soloResult={game?.config?.solo_result}
              />
            ) : (
              <MetaCard
                launchAt={game?.cree_le}
                endAt={game?.termine_le}
                nbJoueurs={stats.nb_joueurs}
              />
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {stats.sous_themes.map(st => (
              <StatCard
                key={st.id}
                title={st.nom}
                correctes={st.correctes}
                timeouts={st.timeouts ?? 0}
                incorrectes={st.incorrectes ?? 0}
                total={st.total}
                isCategory
                onClick={() => setDrill({ level: 'st', ...st })}
              />
            ))}
          </div>
        </div>
      )
    }

    // Niveau 1 — Grille questions d'un sous-thème
    if (drill.level === 'st') {
      return (
        <div className="space-y-3">
          <NavBanner
            label={drill.nom}
            breadcrumb={<button onClick={() => setDrill(null)} className="hover:text-white/55 transition-colors">Sous-thèmes ›</button>}
            correctes={drill.correctes} timeouts={drill.timeouts ?? 0} incorrectes={drill.incorrectes ?? 0} total={drill.total}
            onBack={() => setDrill(null)}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(drill.questions ?? []).map(q => (
              <StatCard
                key={q.id}
                title={q.enonce}
                correctes={q.correctes}
                timeouts={q.timeouts ?? 0}
                incorrectes={q.incorrectes ?? 0}
                total={q.total}
                onClick={() => setDrill({ level: 'q', stId: drill.id, stNom: drill.nom, ...q })}
              />
            ))}
          </div>
        </div>
      )
    }

    // Niveau 2 — Distribution A/B/C/D d'une question
    if (drill.level === 'q') {
      const reponses = drill.reponses ?? []
      const dist     = drill.distribution ?? [0, 0, 0, 0]
      const totalAnswers = dist.reduce((s, n) => s + n, 0)
      return (
        <div className="space-y-3">
          <NavBanner
            label={drill.enonce}
            breadcrumb={
              <>
                <button onClick={() => setDrill(null)} className="hover:text-white/55 transition-colors">Sous-thèmes</button>
                {' › '}
                <button
                  onClick={() => { const st = stats.sous_themes.find(s => s.id === drill.stId); if (st) setDrill({ level: 'st', ...st }) }}
                  className="hover:text-white/55 transition-colors"
                >
                  {drill.stNom}
                </button>
              </>
            }
            correctes={drill.correctes} timeouts={drill.timeouts ?? 0} incorrectes={drill.incorrectes ?? 0} total={drill.total}
            onBack={() => {
              const st = stats.sous_themes.find(s => s.id === drill.stId)
              if (st) setDrill({ level: 'st', ...st })
            }}
          />
          <div className="grid grid-cols-2 gap-3">
            {reponses.map((rep, i) => (
              <AnswerCard
                key={i}
                texte={typeof rep === 'string' ? rep : (rep?.texte ?? '')}
                count={dist[i] ?? 0}
                total={totalAnswers}
                isCorrect={i === drill.bonne_reponse}
              />
            ))}
          </div>
        </div>
      )
    }

    return null
  }

  return (
    <div className="h-screen flex flex-col select-none bg-gray-900">
      <HostHeader
        code={code}
        deckTitle={game?.decks?.titre ?? '…'}
        statut={statut}
        playerCount={0}
        maxPlayers={game?.config?.max_joueurs ?? GAME_DEFAULTS.MAX_PLAYERS}
        onGameUpdate={handleGameUpdate}
      />

      <div className="flex flex-1 min-h-0">
        <HostSideNav
          code={code}
          active="stats"
          statut={statut}
          deckTitle={game?.decks?.titre ?? ''}
          onGameUpdate={handleGameUpdate}
          isSolo={isSolo}
          titleBarRight={!isSolo ? (
            <div className="flex gap-0.5 p-1 bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-xl">
              <button
                onClick={() => handleModeChange('global')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  mode === 'global' ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'
                }`}
              >
                Vue globale
              </button>
              <button
                onClick={() => handleModeChange('par-joueur')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  mode === 'par-joueur' ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'
                }`}
              >
                Par joueur
              </button>
            </div>
          ) : null}
        />

        <main className="flex-1 overflow-y-auto p-4 pb-20 sm:pb-4 sm:pt-14 space-y-4">
          {/* Toggle mobile uniquement — sur desktop il est dans le bandeau titre */}
          {!isSolo && (
            <div className="flex sm:hidden gap-0.5 p-1 bg-gray-800 border border-white/10 rounded-xl w-fit">
              <button
                onClick={() => handleModeChange('global')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  mode === 'global' ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'
                }`}
              >
                Vue globale
              </button>
              <button
                onClick={() => handleModeChange('par-joueur')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  mode === 'par-joueur' ? 'bg-white/15 text-white' : 'text-white/40 hover:text-white/70'
                }`}
              >
                Par joueur
              </button>
            </div>
          )}
          {mode === 'par-joueur' ? renderParJoueur() : renderContent()}
        </main>
      </div>
    </div>
  )
}
